import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FileArrowDown } from '@phosphor-icons/react';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import { weeklyReportsEndpoints } from '../../endpoints/weeklyReports';
import CustomTable from '../../shared/CustomTable/CustomTable';
import styles from './WeeklyReports.module.scss';

/**
 * Viewer singolo report settimanale in web format.
 * Spec v1.1.1 sez 14.
 */
export const WeeklyReportView: FC<{
  reportId: string;
  onExport: (id: string) => void;
}> = ({ reportId, onExport }) => {
  const { data: report, isLoading } = useQuery({
    queryKey: ['weeklyReport', reportId],
    queryFn: () =>
      weeklyReportsEndpoints.get(reportId).then((r) => r.data),
  });

  if (isLoading || !report) {
    return (
      <div className={styles.viewerLoader}>
        <LeopartLoader size="sm" />
      </div>
    );
  }

  const s = report.summary;
  const trends = report.trendsVsPreviousWeek;

  return (
    <div className={styles.reportDoc}>
      <div className={styles.reportHeader}>
        <div>
          <Typhography type="h2" size="bold-20">
            Settimana {report.weekNumber} · {report.season}
          </Typhography>
          <div className={styles.reportSub}>
            {dayjs(report.startDate).format('D MMMM')} –{' '}
            {dayjs(report.endDate).format('D MMMM YYYY')}
          </div>
        </div>
        <CustomButton
          color="outline"
          size="sm"
          onClick={() => onExport(reportId)}
        >
          <FileArrowDown size={14} /> Esporta PDF
        </CustomButton>
      </div>

      {/* Sintesi esecutiva */}
      <Section title="Sintesi esecutiva">
        <div className={styles.kpiGrid}>
          <Kpi
            label="Sessioni"
            value={`${s.sessionsExecuted}/${s.sessionsPlanned}`}
          />
          <Kpi label="Durata totale" value={`${s.totalDuration ?? 0}'`} />
          <Kpi label="EWT" value={`${s.totalEWT ?? 0}'`} />
          <Kpi label="CE totale" value={(s.totalCE ?? 0).toFixed(0)} />
          <Kpi label="IPS medio" value={(s.avgIPS ?? 0).toFixed(1)} />
          <Kpi label="CLS medio" value={(s.avgCLS ?? 0).toFixed(1)} />
          <Kpi label="ACWR" value={(s.acwrEnd ?? 0).toFixed(2)} />
          <Kpi label="Monotonia" value={(s.monotony ?? 0).toFixed(2)} />
          <Kpi label="Strain" value={(s.strain ?? 0).toFixed(0)} />
        </div>
        {s.narrativeText && (
          <div className={styles.narrative}>{s.narrativeText}</div>
        )}
      </Section>

      {/* Athlete breakdown */}
      {report.athleteBreakdown && report.athleteBreakdown.length > 0 && (
        <Section title="Carico per atleta">
          <CustomTable
            headers={['Atleta', 'Sessioni', 'Minuti', 'CE', 'AU', 'Status']}
            data={report.athleteBreakdown}
            fields={{
              Atleta: {
                key: 'firstName',
                customComponent: (_: any, a) => (
                  <span>
                    {a.firstName} {a.lastName}
                  </span>
                ),
              },
              Sessioni: {
                key: 'sessionsExecuted',
                customComponent: (_: any, a) => (
                  <span>
                    {a.sessionsExecuted ?? '—'}/{a.sessionsPlanned ?? '—'}
                  </span>
                ),
              },
              Minuti: {
                key: 'totalMinutes',
                customValue: (value: number | undefined) => value ?? '—',
              },
              CE: { key: 'totalCE', customValue: (value: number | undefined) => (value ?? 0).toFixed(0) },
              AU: { key: 'totalAU', customValue: (value: number | undefined) => (value ?? 0).toFixed(0) },
              Status: {
                key: 'status',
                customComponent: (_: any, a) => <StatusPill status={a.status ?? 'ok'} />,
              },
            }}
          />
        </Section>
      )}

      {/* Match summary */}
      {report.matchSummary && report.matchSummary.matchSessionId && (
        <Section title="Partita">
          <div className={styles.matchBox}>
            <div>
              <strong>{report.matchSummary.opponent ?? '—'}</strong> ·{' '}
              {report.matchSummary.result ?? 'In attesa'}
            </div>
            {report.matchSummary.avgMinutesPlayed != null && (
              <div>
                Minuti giocati medi: {report.matchSummary.avgMinutesPlayed}'
              </div>
            )}
          </div>
        </Section>
      )}

      {/* GPS comparison */}
      {report.gpsComparison && report.gpsComparison.length > 0 && (
        <Section title="Confronto GPS">
          <CustomTable
            headers={['Sessione', 'IPS pianif.', 'IPS GPS', 'Delta']}
            data={report.gpsComparison}
            fields={{
              Sessione: { key: 'dayLabel' },
              'IPS pianif.': {
                key: 'plannedIPS',
                customValue: (value: number | undefined) => value?.toFixed(1) ?? '—',
              },
              'IPS GPS': {
                key: 'gpsIPS',
                customValue: (value: number | undefined) => value?.toFixed(1) ?? '—',
              },
              Delta: {
                key: 'deltaPercent',
                customValue: (value: number | undefined) =>
                  value != null ? `${value > 0 ? '+' : ''}${value.toFixed(0)}%` : '—',
              },
            }}
          />
        </Section>
      )}

      {/* Atleti a rischio */}
      {report.atRiskAthletes && report.atRiskAthletes.length > 0 && (
        <Section title="Atleti a rischio">
          <ul className={styles.riskList}>
            {report.atRiskAthletes.map((a, i) => (
              <li
                key={i}
                className={`${styles.riskItem} ${
                  a.severity === 'critical' ? styles.riskCritical : ''
                }`}
              >
                <strong>
                  {a.severity === 'critical' ? 'CRITICO' : 'ATTENZIONE'}
                </strong>{' '}
                — {a.reason}
                {a.suggestion && (
                  <div className={styles.riskSuggestion}>
                    → {a.suggestion}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Trend */}
      {trends && (
        <Section title="Trend vs settimana precedente">
          <div className={styles.trendGrid}>
            {renderTrend('CE', trends.ce)}
            {renderTrend('IPS', trends.ips)}
            {renderTrend('CLS', trends.cls)}
            {renderTrend('Monotonia', trends.monotony)}
            {renderTrend('Strain', trends.strain)}
          </div>
          {trends.narrativeText && (
            <div className={styles.narrative}>{trends.narrativeText}</div>
          )}
        </Section>
      )}

      {/* Suggerimenti */}
      {report.nextWeekSuggestions &&
        report.nextWeekSuggestions.length > 0 && (
          <Section title="Suggerimenti settimana prossima">
            <ul className={styles.suggList}>
              {report.nextWeekSuggestions.map((sug, i) => (
                <li key={i}>{sug}</li>
              ))}
            </ul>
          </Section>
        )}
    </div>
  );
};

const Section: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.sectionBody}>{children}</div>
  </div>
);

const Kpi: FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className={styles.kpi}>
    <span className={styles.kpiLabel}>{label}</span>
    <span className={styles.kpiValue}>{value}</span>
  </div>
);

const StatusPill: FC<{ status: string }> = ({ status }) => {
  const label: Record<string, string> = {
    ok: 'OK',
    low_load: 'Carico basso',
    at_risk: 'A rischio',
    injured: 'Infortunato',
  };
  return (
    <span
      className={`${styles.statusPill} ${
        status === 'at_risk' || status === 'injured'
          ? styles.statusRisk
          : status === 'low_load'
            ? styles.statusLow
            : styles.statusOk
      }`}
    >
      {label[status] ?? status}
    </span>
  );
};

function renderTrend(
  label: string,
  t?: { current: number; previous: number; delta: number },
) {
  if (!t) return null;
  const arrow = t.delta > 1 ? '↗' : t.delta < -1 ? '↘' : '→';
  return (
    <div className={styles.trendItem}>
      <span className={styles.trendLabel}>{label}</span>
      <span className={styles.trendValue}>
        {t.previous.toFixed(1)} → {t.current.toFixed(1)} {arrow}{' '}
        <em>{t.delta >= 0 ? '+' : ''}{t.delta.toFixed(1)}%</em>
      </span>
    </div>
  );
}
